import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Alert,
  Box,
  CircularProgress,
  Paper,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import { contentApi, csvApi } from '../services/api';

interface CourseMapping {
  filename: string;
  categoryPath: string;
}

interface CourseMappingEditorProps {
  open: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
}

export const CourseMappingEditor: FC<CourseMappingEditorProps> = ({
  open,
  onClose,
  onSaveSuccess,
}) => {
  const [mappings, setMappings] = useState<CourseMapping[]>([]);
  const [courses, setCourses] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unmappedCourses, setUnmappedCourses] = useState<string[]>([]);
  const [bulkCategory, setBulkCategory] = useState<string>('');

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  useEffect(() => {
    // Update unmapped courses whenever courses or mappings change
    const mappedFiles = new Set(mappings.map(m => m.filename));
    const unmapped = courses.filter(course => !mappedFiles.has(course));
    setUnmappedCourses(unmapped);
  }, [courses, mappings]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load existing mappings if they exist
      try {
        const data = await csvApi.parseCSV('root', 'course-mappings.csv');
        const mappingRows: CourseMapping[] = data.rows.map((row: any) => ({
          filename: row.filename || '',
          categoryPath: row['category path'] || row.categoryPath || '',
        }));
        setMappings(mappingRows);
      } catch (err) {
        // File doesn't exist yet, start with empty
        setMappings([]);
      }

      // Load courses from courses folder
      const courseFiles = await contentApi.getFolderContents('courses');
      const courseNames = courseFiles
        .filter((f) => f.name.endsWith('.mbz'))
        .map((f) => f.name)
        .sort();
      setCourses(courseNames);

      // Load categories from categories.csv
      try {
        const categoriesData = await csvApi.parseCSV('categories', 'categories.csv');
        const categoryNames = categoriesData.rows
          .map((row: any) => row.category)
          .filter((name: string) => name && name.trim())
          .sort();
        setCategories(categoryNames);
      } catch (err) {
        // No categories file yet
        setCategories([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    }
    setLoading(false);
  };

  const handleAddRow = () => {
    setMappings([
      ...mappings,
      {
        filename: '',
        categoryPath: '',
      },
    ]);
  };

  const handleDeleteRow = (index: number) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  const handleRowChange = (index: number, field: keyof CourseMapping, value: string) => {
    const newMappings = [...mappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setMappings(newMappings);
  };

  const handleAutoMap = () => {
    // Auto-map unmapped courses to their first matching category or prompt for bulk assignment
    const newMappings = [...mappings];
    
    unmappedCourses.forEach(course => {
      // Try to find a category that might match the course name
      let suggestedCategory = categories[0] || ''; // Default to first category
      
      // Simple matching logic - you can make this more sophisticated
      const courseLower = course.toLowerCase();
      const matchingCategory = categories.find(cat => 
        courseLower.includes(cat.toLowerCase()) || cat.toLowerCase().includes(courseLower.split('-')[0])
      );
      
      if (matchingCategory) {
        suggestedCategory = matchingCategory;
      }
      
      newMappings.push({
        filename: course,
        categoryPath: suggestedCategory
      });
    });
    
    setMappings(newMappings);
  };

  const handleMapAllToCategory = () => {
    if (!bulkCategory || unmappedCourses.length === 0) return;
    
    const newMappings = [...mappings];
    unmappedCourses.forEach(course => {
      newMappings.push({
        filename: course,
        categoryPath: bulkCategory
      });
    });
    
    setMappings(newMappings);
    setBulkCategory('');
  };

  const handleSave = async () => {
    // Filter out empty rows
    const filledMappings = mappings.filter((m) => m.filename.trim() && m.categoryPath.trim());

    if (filledMappings.length === 0) {
      setError('At least one course mapping is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const headers = ['filename', 'category path'];
      const rows = filledMappings.map((m) => ({
        filename: m.filename,
        'category path': m.categoryPath,
      }));

      await csvApi.updateCSV('root', 'course-mappings.csv', {
        headers,
        rows: rows as any,
      });

      onSaveSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          if (unmappedCourses.length > 0 && categories.length > 0) {
            handleAutoMap();
          }
          break;
        case 'a':
          if (event.shiftKey) {
            event.preventDefault();
            handleAddRow();
          }
          break;
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth onKeyDown={handleKeyDown}>
      <DialogTitle>
        Map Courses to Categories
        <Box component="span" sx={{ fontSize: '0.8em', color: 'text.secondary', ml: 2 }}>
          Shortcuts: Ctrl+Enter (Auto-map), Ctrl+Shift+A (Add row)
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {courses.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No courses found in the courses folder. Upload some .mbz files first.
              </Alert>
            )}

            {categories.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No categories defined yet. Create categories first using the Categories editor.
              </Alert>
            )}

            {courses.length > 0 && categories.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Progress:</strong> {mappings.filter(m => m.filename && m.categoryPath).length} of {courses.length} courses mapped
                {unmappedCourses.length > 0 && (
                  <span> • {unmappedCourses.length} courses need mapping</span>
                )}
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddRow}
                  variant="outlined"
                  disabled={saving || courses.length === 0 || categories.length === 0}
                >
                  Add Mapping
                </Button>

                {unmappedCourses.length > 0 && (
                  <>
                    <Chip 
                      label={`${unmappedCourses.length} unmapped courses`} 
                      color="warning" 
                      size="small" 
                    />
                    
                    <Button
                      startIcon={<AutoAwesomeIcon />}
                      onClick={handleAutoMap}
                      variant="outlined"
                      color="secondary"
                      disabled={saving || categories.length === 0}
                      size="small"
                    >
                      Auto-Map
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Map all to category</InputLabel>
                        <Select
                          value={bulkCategory}
                          onChange={(e) => setBulkCategory(e.target.value)}
                          label="Map all to category"
                          disabled={saving}
                        >
                          {categories.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <Button
                        startIcon={<SelectAllIcon />}
                        onClick={handleMapAllToCategory}
                        variant="contained"
                        size="small"
                        disabled={!bulkCategory || saving}
                      >
                        Map All
                      </Button>
                    </Box>
                  </>
                )}
              </Stack>
            </Box>

            <Paper variant="outlined" sx={{ overflow: 'auto', maxHeight: 400 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>
                      Course File *
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                      Category Path *
                    </TableCell>
                    <TableCell sx={{ width: '10%', textAlign: 'center' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((mapping, index) => {
                    const isNewMapping = unmappedCourses.includes(mapping.filename) && mapping.categoryPath;
                    return (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: isNewMapping ? 'success.dark' : 'inherit',
                        '&:hover': { bgcolor: isNewMapping ? 'success.main' : 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Autocomplete
                          size="small"
                          value={mapping.filename}
                          onChange={(_, value) =>
                            handleRowChange(index, 'filename', value || '')
                          }
                          inputValue={mapping.filename}
                          onInputChange={(_, value) =>
                            handleRowChange(index, 'filename', value)
                          }
                          options={courses}
                          disabled={saving}
                          freeSolo
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select a course file"
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          value={mapping.categoryPath}
                          onChange={(_, value) =>
                            handleRowChange(index, 'categoryPath', value || '')
                          }
                          inputValue={mapping.categoryPath}
                          onInputChange={(_, value) =>
                            handleRowChange(index, 'categoryPath', value)
                          }
                          options={categories}
                          disabled={saving}
                          freeSolo
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="e.g., Science or STEM/Science"
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRow(index)}
                          disabled={saving}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>

            <Box sx={{ mt: 2, fontSize: '0.875rem', color: '#666' }}>
              <p>
                <strong>How it works:</strong>
              </p>
              <ul>
                <li>
                  <strong>Course File:</strong> The .mbz backup file name (e.g.,
                  backup-moodle2-course-58-threatamp.mbz)
                </li>
                <li>
                  <strong>Category Path:</strong> Where to restore the course. Use category
                  name for root level, or &quot;Parent/Child&quot; for nested categories
                </li>
                <li>
                  This mapping is used during Moodle bootstrap to place courses in the
                  correct categories
                </li>
              </ul>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading || mappings.length === 0}
        >
          {saving ? 'Saving...' : 'Save Mappings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseMappingEditor;
